// Ispis bruto bilance i kartice konta u pdf.
//
// Papir prati ekran: bilanca se ispisuje na odabranoj razini i s odabranim
// skupom kolona, pa je ispis ono sto je korisnik vidio kad je pritisnuo Printaj.
// Drzi se izvan komponente jer sirine kolona traze pravu aritmetiku - u
// detaljnom prikazu devet kolona mora stati na A4 landscape.

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'

const LINE = '#9e9e9e'
const LINE_JAKA = '#4a524f'
const CRVENA = '#8c2f1e'

export const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

export const novac = (v) => num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')

// Nula se na bruto bilanci pise crticom - vecina celija je prazna pa se tako
// odmah vidi koja su konta uopce u igri.
const iznos = (v) => (!v || Math.abs(v) < 0.005 ? '–' : novac(v))

export const datum = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '')

// A4 landscape minus margine.
const PAGE_WIDTH = 841.89 - 2 * 18
const V_LINE = 0.4

// pdfmake dodaje padding celija i okomite linije povrh sirina iz `widths`.
// Layout ispod stavlja padding na nulu i pada kroz margine teksta, pa se s
// budzeta skidaju samo linije.
const usableWidth = (columnCount) => PAGE_WIDTH - (columnCount + 1) * V_LINE

// Naziv uzima sav visak sirine; ako ni tako ne stane, sve se kolone smanje
// istim faktorom da tablica sjedne na stranicu umjesto da izadje s ruba.
// Bez sirine racuna se za A4 landscape; uspravni dopisi predaju svoju.
export const rasporedi = (cols, sirina) => {
  const usable = sirina ? sirina - (cols.length + 1) * V_LINE : usableWidth(cols.length)
  const flex = cols.find(c => c.flex)
  const ukupno = cols.reduce((s, c) => s + c.w, 0)
  if (ukupno > usable) {
    const scale = usable / ukupno
    cols.forEach(c => { c.w = Math.floor(c.w * scale * 10) / 10 })
  } else if (flex) {
    flex.w += usable - ukupno
  }
  return cols
}

export const zaglavlje = (company, naslov, podnaslov, uvjeti, desno) => ({
  columns: [
    {
      width: '30%',
      text: [
        {text: (company.CompanyNameShort || company.CompanyName || '') + ' \n', fontSize: 10, bold: true},
        {text: (company.CompanyStreet || '') + ' ' + (company.CompanyBuildingNameNumber || '') + ' \n', fontSize: 7},
        {text: (company.CompanyPostcode || '') + ' ' + (company.CompanyCity || '') + ' \n', fontSize: 7},
        {text: 'OIB: ' + (company.CompanyVATID || ''), fontSize: 7}
      ]
    },
    {
      width: '48%',
      text: [
        {text: naslov + ' \n', fontSize: 15, bold: true},
        {text: podnaslov + ' \n', fontSize: 9},
        {text: uvjeti, fontSize: 7, color: '#555555'}
      ],
      alignment: 'center'
    },
    {
      width: '22%',
      text: desno.map((t, i) => ({text: t + (i < desno.length - 1 ? ' \n' : ''), fontSize: 7})),
      alignment: 'right'
    }
  ],
  columnGap: 8,
  margin: [0, 0, 0, 8]
})

// Lijevo ide sto je ispisano, desno koja je stranica - istim slovima, da se
// otrgnuta stranica moze prepoznati i bez prve.
export const podnozje = (lijevo) => (currentPage, pageCount) => ({
  columns: [
    {
      width: '*',
      text: lijevo,
      fontSize: 6,
      color: '#666666',
      alignment: 'left'
    },
    {
      width: 'auto',
      text: 'Stranica ' + currentPage + ' od ' + pageCount,
      fontSize: 6,
      alignment: 'right'
    }
  ],
  columnGap: 12,
  margin: [18, 6, 18, 0]
})

export const layoutTablice = (jakeLinije) => ({
  hLineWidth: (i, node) => {
    if (i === 0 || i === node.table.body.length) { return 0.8 }
    return jakeLinije && jakeLinije.indexOf(i) !== -1 ? 0.8 : 0.4
  },
  vLineWidth: () => V_LINE,
  hLineColor: (i) => (jakeLinije && jakeLinije.indexOf(i) !== -1 ? LINE_JAKA : LINE),
  vLineColor: () => LINE,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0
})

// ---------------------------------------------------------------------------
// Bruto bilanca
// ---------------------------------------------------------------------------

const koloneBilance = (detaljno) => {
  const cols = detaljno
    ? [
      {key: 'code', head: 'Konto', w: 42, align: 'left'},
      {key: 'name', head: 'Naziv konta', w: 120, align: 'left', flex: true},
      {key: 'psD', head: 'Duguje', grupa: 'Početno stanje', w: 84, align: 'right'},
      {key: 'psP', head: 'Potražuje', grupa: 'Početno stanje', w: 84, align: 'right'},
      {key: 'prD', head: 'Duguje', grupa: 'Promet u razdoblju', w: 84, align: 'right'},
      {key: 'prP', head: 'Potražuje', grupa: 'Promet u razdoblju', w: 84, align: 'right'},
      {key: 'totD', head: 'Duguje', grupa: 'Ukupno', w: 84, align: 'right'},
      {key: 'totP', head: 'Potražuje', grupa: 'Ukupno', w: 84, align: 'right'},
      {key: 'saldo', head: 'Saldo', grupa: 'Ukupno', w: 92, align: 'right'}
    ]
    : [
      {key: 'code', head: 'Konto', w: 46, align: 'left'},
      {key: 'name', head: 'Naziv konta', w: 160, align: 'left', flex: true},
      {key: 'totD', head: 'Duguje', w: 110, align: 'right'},
      {key: 'totP', head: 'Potražuje', w: 110, align: 'right'},
      {key: 'saldo', head: 'Saldo', w: 120, align: 'right'}
    ]
  return rasporedi(cols)
}

// Objedinjeni naslov iznad para kolona, isto kao na ekranu.
const redGrupa = (cols) => {
  const red = []
  let i = 0
  while (i < cols.length) {
    const g = cols[i].grupa
    if (!g) {
      red.push({text: ''})
      i += 1
      continue
    }
    let span = 0
    while (i + span < cols.length && cols[i + span].grupa === g) { span += 1 }
    red.push({
      text: g,
      colSpan: span,
      alignment: 'center',
      bold: true,
      fontSize: 7,
      margin: [2, 3, 2, 3]
    })
    for (let k = 1; k < span; k += 1) { red.push({text: ''}) }
    i += span
  }
  return red
}

// Nijedan ispis nema podloge - puna traka preko stranice trosi nerazmjerno
// puno tonera, a debljina slova i crte odvajaju redak jednako dobro.
export const redNaslova = (cols, fs) => cols.map(c => ({
  text: c.head,
  fontSize: fs,
  bold: true,
  alignment: c.align,
  margin: [2, 3, 2, 3]
}))

// Razina se na papiru vidi kao na ekranu: uvlaka, debljina i velicina slova.
const stilRazine = (level) => {
  if (level === 1) { return {bold: true, size: 8, uvlaka: 0, verzal: true} }
  if (level === 2) { return {bold: true, size: 7.5, uvlaka: 6} }
  if (level === 3) { return {bold: false, size: 7.5, uvlaka: 12, kurziv: true} }
  return {bold: false, size: 7.5, uvlaka: 18}
}

const redBilance = (cols, cvor) => {
  const s = stilRazine(cvor.level)
  return cols.map(c => {
    if (c.key === 'code') {
      return {
        text: cvor.code,
        fontSize: s.size,
        bold: s.bold,
        alignment: 'left',
        margin: [2, 1.5, 2, 1.5]
      }
    }
    if (c.key === 'name') {
      return {
        text: s.verzal ? String(cvor.name || '').toUpperCase() : (cvor.name || ''),
        fontSize: s.size,
        bold: s.bold,
        italics: s.kurziv === true,
        alignment: 'left',
        margin: [2 + s.uvlaka, 1.5, 2, 1.5]
      }
    }
    if (c.key === 'saldo') {
      const v = cvor.saldo
      const prazno = Math.abs(v) < 0.005
      return {
        text: prazno ? '–' : novac(Math.abs(v)) + ' ' + (v > 0 ? 'D' : 'P'),
        fontSize: s.size,
        bold: s.bold,
        color: v < 0 ? CRVENA : undefined,
        alignment: 'right',
        margin: [2, 1.5, 2, 1.5]
      }
    }
    return {
      text: iznos(cvor[c.key]),
      fontSize: s.size,
      bold: s.bold,
      alignment: 'right',
      margin: [2, 1.5, 2, 1.5]
    }
  })
}

const redUkupno = (cols, zbroj, valuta) => cols.map((c, index) => {
  let text = ''
  if (index === 0) { text = '' }
  else if (index === 1) { text = 'UKUPNO ' + valuta }
  else if (c.key === 'saldo') { text = novac(zbroj.saldo) }
  else { text = novac(zbroj[c.key]) }
  return {
    text: text,
    fontSize: 8,
    bold: true,
    alignment: index <= 1 ? 'left' : 'right',
    margin: [2, 3, 2, 3]
  }
})

/**
 * @param {Object} ctx
 *   redci     cvorovi koje lista trenutno pokazuje, istim redom
 *   zbroj     {psD, psP, prD, prP, totD, totP, saldo}
 *   company   store.companyActiv
 *   detaljno  true za devet kolona, false za pet
 *   razinaOpis, fromDate, toDate, fromAccount, toAccount, valuta, brojKonta
 */
export const buildBalancePdf = (ctx) => {
  const redci = ctx.redci || []
  const zbroj = ctx.zbroj || {}
  const detaljno = ctx.detaljno === true
  const cols = koloneBilance(detaljno)
  const valuta = ctx.valuta || 'EUR'
  // Jedan trenutak za cijeli dokument - zaglavlje i podnozje moraju pisati isto
  // i kad se ispis gradi preko granice minute.
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = []
  if (detaljno) { body.push(redGrupa(cols)) }
  body.push(redNaslova(cols, 7))
  redci.forEach(r => body.push(redBilance(cols, r)))
  body.push(redUkupno(cols, zbroj, valuta))

  const uravnotezena = Math.abs(num(zbroj.saldo)) < 0.005
  const zaglavnihRedaka = detaljno ? 2 : 1

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        'Bruto bilanca',
        valuta + '   ' + datum(ctx.fromDate) + ' – ' + datum(ctx.toDate),
        'konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '') +
          ' · ' + (ctx.razinaOpis || '') + ' · ' + (detaljno ? 'detaljno' : 'sažeto'),
        [
          'Datum ispisa: ' + trenutak,
          redci.length + ' redaka · ' + (ctx.brojKonta || 0) + ' konta'
        ]
      ),
      {
        table: {
          headerRows: zaglavnihRedaka,
          widths: cols.map(c => c.w),
          body: body
        },
        layout: layoutTablice([zaglavnihRedaka, body.length - 1])
      },
      {
        text: uravnotezena
          ? '✓  Duguje = Potražuje  ·  ukupno ' + novac(zbroj.totD) + '  ·  saldo ' + novac(zbroj.saldo)
          : '✕  Bilanca nije u ravnoteži  ·  duguje ' + novac(zbroj.totD) +
            '  ·  potražuje ' + novac(zbroj.totP) + '  ·  saldo ' + novac(zbroj.saldo),
        fontSize: 8,
        bold: true,
        color: uravnotezena ? '#1f6b43' : CRVENA,
        margin: [0, 8, 0, 0]
      }
    ],
    // Trenutak ispisa ide i u podnozje: dva ispisa istog razdoblja izgledaju
    // jednako, pa se listovi bez toga ne mogu razdvojiti ako se pomijesaju.
    footer: podnozje([
      (ctx.company || {}).CompanyNameShort || (ctx.company || {}).CompanyName || '',
      'Bruto bilanca',
      ctx.fromDate && ctx.toDate ? datum(ctx.fromDate) + ' – ' + datum(ctx.toDate) : '',
      ctx.fromAccount || ctx.toAccount
        ? 'konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '') : '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }

  return docDefinition
}

export const printBalancePdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildBalancePdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Kartica konta
// ---------------------------------------------------------------------------

const koloneKartice = () => rasporedi([
  {key: 'BookingDate', head: 'Datum knjiž.', w: 56, align: 'left'},
  {key: 'dokument', head: 'Dokument', w: 68, align: 'left'},
  {key: 'veza', head: 'Veza', w: 62, align: 'left'},
  {key: 'partnerName', head: 'Partner', w: 120, align: 'left', flex: true},
  {key: 'Description', head: 'Opis', w: 120, align: 'left'},
  {key: 'Debit', head: 'Duguje', w: 74, align: 'right'},
  {key: 'Credit', head: 'Potražuje', w: 74, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 80, align: 'right'}
])

const redKartice = (cols, item) => cols.map(c => {
  let text = ''
  if (c.key === 'BookingDate') { text = datum(item.BookingDate) }
  else if (c.key === 'Debit' || c.key === 'Credit') { text = item[c.key] ? novac(item[c.key]) : '' }
  else if (c.key === 'saldo') { text = novac(item.saldo) }
  else { text = item[c.key] === null || item[c.key] === undefined ? '' : String(item[c.key]) }
  return {
    text: text,
    fontSize: 7.5,
    alignment: c.align,
    color: c.key === 'saldo' && num(item.saldo) < 0 ? CRVENA : undefined,
    margin: [2, 1.5, 2, 1.5]
  }
})

// Redak preko cijele sirine s iznosima u zadnje tri kolone.
//
// Bez pozadinske boje - puna traka preko stranice trosi nerazmjerno puno tonera,
// a debljina slova i crta iznad odvajaju redak jednako dobro.
const redZbroja = (cols, naslov, d, p) => {
  const spanDo = cols.length - 3
  const celija = (text, align) => ({
    text: text,
    fontSize: 8,
    bold: true,
    alignment: align,
    margin: [2, 3, 2, 3]
  })
  // Praznina iza colSpana mora imati `text`. Bez njega pdfmake baca
  // "Unrecognized document structure" tek kad se dokument stvarno gradi.
  const red = [Object.assign(celija(naslov, 'left'), {colSpan: spanDo})]
  for (let i = 1; i < spanDo; i += 1) { red.push({text: ''}) }
  red.push(celija(novac(d), 'right'))
  red.push(celija(novac(p), 'right'))
  red.push(celija(novac(d - p), 'right'))
  return red
}

/**
 * @param {Object} ctx
 *   konto       {account4, account4Name}
 *   stavke      redci kartice, istim redom kao na ekranu, sa `saldo`
 *   psDebit, psCredit, prometDebit, prometCredit
 *   company, fromDate, toDate, valuta
 */
export const buildAccountCardPdf = (ctx) => {
  const cols = koloneKartice()
  const stavke = ctx.stavke || []
  const konto = ctx.konto || {}
  const valuta = ctx.valuta || 'EUR'
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')
  const psD = num(ctx.psDebit)
  const psP = num(ctx.psCredit)
  const prD = num(ctx.prometDebit)
  const prP = num(ctx.prometCredit)

  const body = [redNaslova(cols, 7)]
  body.push(redZbroja(cols, 'Početno stanje', psD, psP))
  stavke.forEach(s => body.push(redKartice(cols, s)))
  body.push(redZbroja(cols, 'Promet u razdoblju', prD, prP))
  body.push(redZbroja(cols, 'Ukupno', psD + prD, psP + prP))

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        'Kartica konta ' + (konto.account4 || ''),
        konto.account4Name || '',
        valuta + ' · ' + datum(ctx.fromDate) + ' – ' + datum(ctx.toDate),
        [
          'Datum ispisa: ' + trenutak,
          stavke.length + ' stavaka'
        ]
      ),
      {
        table: {
          headerRows: 1,
          widths: cols.map(c => c.w),
          body: body
        },
        layout: layoutTablice([1, 2, body.length - 2, body.length - 1])
      },
      {
        text: 'Početno stanje je stanje konta na početak razdoblja. Stavke su knjiženja ' +
          'razdoblja bez temeljnice početnog stanja, pa je Ukupno zbroj početnog stanja i prometa.',
        fontSize: 6,
        color: '#666666',
        margin: [0, 6, 0, 0]
      }
    ],
    footer: podnozje([
      (ctx.company || {}).CompanyNameShort || (ctx.company || {}).CompanyName || '',
      'Kartica konta ' + (konto.account4 || ''),
      konto.account4Name || '',
      ctx.fromDate && ctx.toDate ? datum(ctx.fromDate) + ' – ' + datum(ctx.toDate) : '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }

  return docDefinition
}

export const printAccountCardPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildAccountCardPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Financijski izvjestaj - racun dobitka i gubitka i GFI-POD bilanca
//
// Oba izvjestaja daju retke istog oblika (col1 oznaka, col2 konta, col3 naziv,
// col5 prethodna godina, col6 tekuca) pa dijele isti ispis. Razlikuje ih naslov.
// ---------------------------------------------------------------------------

// Uzi je od bilance - pet kolona stane na A4 portrait, sto je i oblik u kojem
// se ovaj izvjestaj obicno prilaze.
const RDG_WIDTH = 595.28 - 2 * 18

// Kolona s kontima ide u ispis samo ako se izricito zatrazi. Iz nje se cita
// kako je RDG sastavljen, pa po zadanom izostaje.
const koloneIzvjestaja = (sKontima) => {
  const cols = [
    {key: 'col1', head: 'Ozn.', w: 30, align: 'left'},
    {key: 'col2', head: 'Konta iz RRiF-ovog plana', w: 118, align: 'left'},
    {key: 'col3', head: 'Naziv pozicije', w: 120, align: 'left', flex: true},
    {key: 'col5', head: '', w: 82, align: 'right'},
    {key: 'col6', head: '', w: 82, align: 'right'}
  ].filter(c => sKontima === true || c.key !== 'col2')
  const usable = RDG_WIDTH - (cols.length + 1) * V_LINE
  const flex = cols.find(c => c.flex)
  const ukupno = cols.reduce((s, c) => s + c.w, 0)
  if (ukupno > usable) {
    const scale = usable / ukupno
    cols.forEach(c => { c.w = Math.floor(c.w * scale * 10) / 10 })
  } else {
    flex.w += usable - ukupno
  }
  return cols
}

// Iste tri razine kao na ekranu: odjeljak, pozicija, podredak.
const stilRetkaIzvjestaja = (r) => {
  if (r.header) { return {bold: true, size: 8, uvlaka: 0, verzal: true} }
  if (r.bold) { return {bold: true, size: 7.5, uvlaka: 6} }
  return {bold: false, size: 7.5, uvlaka: 14}
}

const redIzvjestaja = (cols, r) => {
  const s = stilRetkaIzvjestaja(r)
  const naziv = String(r.col3 || '').replace(/^[\t\s]+/, '')
  return cols.map(c => {
    if (c.key === 'col3') {
      return {
        text: s.verzal ? naziv.toUpperCase() : naziv,
        fontSize: s.size,
        bold: s.bold,
        alignment: 'left',
        margin: [2 + s.uvlaka, 1.5, 2, 1.5]
      }
    }
    if (c.key === 'col5' || c.key === 'col6') {
      const v = num(r[c.key])
      return {
        text: iznos(v),
        fontSize: s.size,
        bold: s.bold,
        color: v < 0 ? CRVENA : undefined,
        alignment: 'right',
        margin: [2, 1.5, 2, 1.5]
      }
    }
    return {
      text: r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key]),
      fontSize: c.key === 'col2' ? s.size - 0.5 : s.size,
      bold: s.bold,
      alignment: 'left',
      margin: [2, 1.5, 2, 1.5]
    }
  })
}

/**
 * @param {Object} ctx  redci (gotovi iz rdg2), company, fromDate, toDate,
 *                      valuta, tekucaGodina, prethodnaGodina, naslov, sKontima
 */
export const buildStatementPdf = (ctx) => {
  const cols = koloneIzvjestaja(ctx.sKontima === true)
  const redci = ctx.redci || []
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const naslov = ctx.naslov || 'Račun dobitka i gubitka'
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  // Zadnje dvije kolone su uvijek godine, bez obzira ima li kolone s kontima.
  cols[cols.length - 2].head = String(ctx.prethodnaGodina || 'Prethodna godina')
  cols[cols.length - 1].head = String(ctx.tekucaGodina || 'Tekuća godina')

  const body = [redNaslova(cols, 7)]
  redci.forEach(r => body.push(redIzvjestaja(cols, r)))

  // Deblja crta iznad svakog odjeljka, isto kao na ekranu.
  const jake = [1]
  redci.forEach((r, i) => { if (r.header) { jake.push(i + 1) } })

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        company,
        naslov,
        valuta + '   ' + datum(ctx.fromDate) + ' – ' + datum(ctx.toDate),
        'po RRiF-ovom kontnom planu',
        ['Datum ispisa: ' + trenutak, redci.length + ' pozicija']
      ),
      {
        table: {
          headerRows: 1,
          widths: cols.map(c => c.w),
          body: body
        },
        layout: layoutTablice(jake)
      }
    ],
    footer: podnozje([
      company.CompanyNameShort || company.CompanyName || '',
      naslov,
      ctx.fromDate && ctx.toDate ? datum(ctx.fromDate) + ' – ' + datum(ctx.toDate) : '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }

  return docDefinition
}

export const printStatementPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildStatementPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Kartica partnera
// ---------------------------------------------------------------------------

const kolonePartnera = () => rasporedi([
  {key: 'dokument', head: 'Dokument', w: 62, align: 'left'},
  {key: 'veza', head: 'Veza', w: 58, align: 'left'},
  {key: 'datum', head: 'Datum knjiž.', w: 54, align: 'left'},
  {key: 'datumDok', head: 'Datum dok.', w: 54, align: 'left'},
  {key: 'dospijece', head: 'Dospijeće', w: 54, align: 'left'},
  {key: 'dana', head: 'Dana', w: 30, align: 'right'},
  {key: 'opis', head: 'Opis', w: 90, align: 'left', flex: true},
  {key: 'konto', head: 'Konto', w: 44, align: 'left'},
  {key: 'debit', head: 'Duguje', w: 72, align: 'right'},
  {key: 'credit', head: 'Potražuje', w: 72, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 78, align: 'right'}
])

const redStavke = (cols, s) => cols.map(c => {
  if (c.key === 'debit' || c.key === 'credit') {
    return {
      text: s[c.key] ? novac(s[c.key]) : '–',
      fontSize: 7,
      alignment: 'right',
      margin: [2, 1.5, 2, 1.5]
    }
  }
  if (c.key === 'saldo') {
    return {
      text: s.saldo === null || s.saldo === undefined ? '' : novac(s.saldo),
      fontSize: 7,
      color: num(s.saldo) < 0 ? CRVENA : undefined,
      alignment: 'right',
      margin: [2, 1.5, 2, 1.5]
    }
  }
  if (c.key === 'dana') {
    return {
      text: s.dana === null || s.dana === undefined ? '' : String(s.dana),
      fontSize: 7,
      bold: num(s.dana) > 0,
      color: num(s.dana) > 0 ? CRVENA : undefined,
      alignment: 'right',
      margin: [2, 1.5, 2, 1.5]
    }
  }
  return {
    text: s[c.key] === null || s[c.key] === undefined ? '' : String(s[c.key]),
    fontSize: 7,
    alignment: c.align,
    margin: [2, 1.5, 2, 1.5]
  }
})

// Zbirni redak grupe - naziv preko prvih osam kolona, iznosi u zadnje tri.
const redGrupePartnera = (cols, g) => {
  const spanDo = cols.length - 3
  const celija = (text, align) => ({
    text: text,
    fontSize: 7.5,
    bold: true,
    alignment: align,
    margin: [2, 3, 2, 3]
  })
  const red = [Object.assign(celija(g.naziv + '  (' + g.stavke.length + ' stavaka)', 'left'), {colSpan: spanDo})]
  for (let i = 1; i < spanDo; i += 1) { red.push({text: ''}) }
  red.push(celija(novac(g.d), 'right'))
  red.push(celija(novac(g.p), 'right'))
  red.push(Object.assign(celija(novac(g.saldo), 'right'),
    num(g.saldo) < 0 ? {color: CRVENA} : {}))
  return red
}

/**
 * @param {Object} ctx  partner {ime, oib}, psGrupa, godine, psSaldo, prometD,
 *                      prometP, ukupnoD, ukupnoP, saldoKraj, brojStavaka,
 *                      company, fromDate, toDate, fromAccount, toAccount, valuta
 */
export const buildPartnerCardPdf = (ctx) => {
  const cols = kolonePartnera()
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const partner = ctx.partner || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  const jake = [1]

  // Papir prati ekran: pocetno stanje se razlaze samo ako je i na ekranu otvoreno.
  if (ctx.psGrupa) {
    body.push(redGrupePartnera(cols, ctx.psGrupa))
    jake.push(body.length - 1)
    if (ctx.psGrupa.open) {
      ctx.psGrupa.stavke.forEach(s => body.push(redStavke(cols, s)))
    }
  }
  ;(ctx.godine || []).forEach(g => {
    body.push(redGrupePartnera(cols, g))
    jake.push(body.length - 1)
    if (g.open) { g.stavke.forEach(s => body.push(redStavke(cols, s))) }
  })

  const ukupno = (() => {
    const spanDo = cols.length - 3
    const celija = (text, align) => ({text: text, fontSize: 8, bold: true, alignment: align, margin: [2, 3, 2, 3]})
    const red = [Object.assign(celija('UKUPNO ' + valuta, 'left'), {colSpan: spanDo})]
    for (let i = 1; i < spanDo; i += 1) { red.push({text: ''}) }
    red.push(celija(novac(ctx.ukupnoD), 'right'))
    red.push(celija(novac(ctx.ukupnoP), 'right'))
    red.push(celija(novac(ctx.saldoKraj), 'right'))
    return red
  })()
  body.push(ukupno)
  jake.push(body.length - 1)

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        company,
        'Kartica partnera',
        (partner.ime || '') + (partner.oib ? '   OIB: ' + partner.oib : ''),
        valuta + ' · ' + datum(ctx.fromDate) + ' – ' + datum(ctx.toDate) +
          ' · konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || ''),
        ['Datum ispisa: ' + trenutak, (ctx.brojStavaka || 0) + ' stavaka']
      ),
      {
        columns: [
          {width: '*', text: [
            {text: 'Početno stanje  ', fontSize: 7, color: '#666666'},
            {text: novac(ctx.psSaldo) + '     ', fontSize: 8, bold: true},
            {text: 'Promet duguje  ', fontSize: 7, color: '#666666'},
            {text: novac(ctx.prometD) + '     ', fontSize: 8, bold: true},
            {text: 'Promet potražuje  ', fontSize: 7, color: '#666666'},
            {text: novac(ctx.prometP) + '     ', fontSize: 8, bold: true},
            {text: 'Saldo  ', fontSize: 7, color: '#666666'},
            {text: novac(ctx.saldoKraj) + ' ' + (num(ctx.saldoKraj) >= 0 ? 'D' : 'P'), fontSize: 9, bold: true}
          ]}
        ],
        margin: [0, 0, 0, 6]
      },
      {
        table: {headerRows: 1, widths: cols.map(c => c.w), body: body},
        layout: layoutTablice(jake)
      },
      {
        text: '„Dana" je razlika između datuma dospijeća i kraja razdoblja. Govori koliko je ' +
          'vremena prošlo od dospijeća, a ne znači da stavka nije plaćena.',
        fontSize: 6,
        color: '#666666',
        margin: [0, 6, 0, 0]
      }
    ],
    footer: podnozje([
      company.CompanyNameShort || company.CompanyName || '',
      'Kartica partnera',
      partner.ime || '',
      ctx.fromDate && ctx.toDate ? datum(ctx.fromDate) + ' – ' + datum(ctx.toDate) : '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }
}

export const printPartnerCardPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildPartnerCardPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Otvorene stavke - Par.Otvoreno, Svi part.otv. i SVI DET.
// ---------------------------------------------------------------------------
//
// Redci dolaze vec slozeni iz otvoreneStavke.js, onim redom i s onim saldom
// koji su bili na ekranu. Saldo nosi stranu D/P prema kontu: na kupcima je
// pozitivan saldo duguje, na dobavljacima potrazuje.

const strana = (v, naDuguje) => {
  if (Math.abs(num(v)) < 0.005) { return '' }
  return (num(v) > 0) === (naDuguje !== false) ? ' D' : ' P'
}
const saldoTekst = (v, naDuguje) => (Math.abs(num(v)) < 0.005 ? '–' : novac(Math.abs(num(v))) + strana(v, naDuguje))
const predznakTekst = (v) => (num(v) < -0.005 ? '−' : '') + novac(Math.abs(num(v)))

const danaTekst = (d) => {
  if (d === null || d === undefined) { return '' }
  if (d > 0) { return String(d) }
  return d === 0 ? 'danas' : 'za ' + (-d)
}

const koloneOtvorenih = () => rasporedi([
  {key: 'vrsta', head: 'Vrsta', w: 34, align: 'left'},
  {key: 'dokument', head: 'Dokument', w: 60, align: 'left'},
  {key: 'veza', head: 'Veza', w: 58, align: 'left'},
  {key: 'datumTxt', head: 'Datum', w: 50, align: 'left'},
  {key: 'dospijeceTxt', head: 'Dospijeće', w: 50, align: 'left'},
  {key: 'dana', head: 'Dana', w: 34, align: 'right'},
  {key: 'opis', head: 'Opis', w: 90, align: 'left', flex: true},
  {key: 'iznos', head: 'Iznos', w: 70, align: 'right'},
  {key: 'zatvoreno', head: 'Zatvoreno', w: 66, align: 'right'},
  {key: 'otvoreno', head: 'Otvoreno', w: 72, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 84, align: 'right'}
])

const redOtvorene = (cols, r, naDuguje) => cols.map(c => {
  const o = {fontSize: 7, alignment: c.align, margin: [2, 1.5, 2, 1.5]}
  if (c.key === 'iznos') { return Object.assign(o, {text: novac(r.iznos)}) }
  if (c.key === 'zatvoreno') { return Object.assign(o, {text: iznos(r.zatvoreno)}) }
  if (c.key === 'otvoreno') {
    return Object.assign(o, {text: (r.jeRacun ? '' : '−') + novac(r.otvoreno), color: r.jeRacun ? undefined : CRVENA})
  }
  if (c.key === 'saldo') { return Object.assign(o, {text: saldoTekst(r.saldo, naDuguje)}) }
  if (c.key === 'dana') {
    return Object.assign(o, {text: danaTekst(r.dana), bold: num(r.dana) > 0, color: num(r.dana) > 0 ? CRVENA : undefined})
  }
  return Object.assign(o, {text: r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key])})
})

// Zbroj: naziv preko svih kolona osim zadnje dvije (otvoreno i saldo).
const redZbrojaOtvorenih = (cols, naslov, saldo, naDuguje, fs) => {
  const spanDo = cols.length - 2
  const celija = (text, align) => ({text: text, fontSize: fs || 7.5, bold: true, alignment: align, margin: [2, 3, 2, 3]})
  const red = [Object.assign(celija(naslov, 'left'), {colSpan: spanDo})]
  for (let i = 1; i < spanDo; i += 1) { red.push({text: ''}) }
  red.push(celija(predznakTekst(saldo), 'right'))
  red.push(celija(saldoTekst(saldo, naDuguje), 'right'))
  return red
}

const redNaslovaGrupe = (cols, g) => {
  const z = g.sazetak || {}
  const red = [{
    text: [
      {text: g.ime || '', bold: true},
      {text: (g.oib ? '    OIB ' + g.oib : '') + '    ·    računa ' + (z.racuna || 0) + ', nepovezanih uplata ' + (z.uplata || 0), color: '#555555'}
    ],
    fontSize: 8,
    colSpan: cols.length,
    alignment: 'left',
    margin: [2, 5, 2, 2]
  }]
  for (let i = 1; i < cols.length; i += 1) { red.push({text: ''}) }
  return red
}

const sazetakOtvorenih = (z, valuta, naDuguje) => ({
  text: [
    {text: 'Otvoreni računi  ', fontSize: 7, color: '#666666'},
    {text: novac(z.iznosRacuna) + ' (' + (z.racuna || 0) + ')     ', fontSize: 8, bold: true},
    {text: 'Nepovezane uplate  ', fontSize: 7, color: '#666666'},
    {text: novac(z.iznosUplata) + ' (' + (z.uplata || 0) + ')     ', fontSize: 8, bold: true},
    {text: 'Dospjelo  ', fontSize: 7, color: '#666666'},
    {text: novac(z.dospjelo) + '     ', fontSize: 8, bold: true},
    {text: 'Saldo  ', fontSize: 7, color: '#666666'},
    {text: saldoTekst(z.saldo, naDuguje) + ' ' + valuta, fontSize: 9, bold: true}
  ],
  margin: [0, 0, 0, 6]
})

const uvjetiOtvorenih = (ctx, valuta) => valuta + ' · stanje na ' + datum(ctx.doDatuma || new Date()) +
  ' · konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '')

const podnozjeOtvorenih = (ctx, dodatak, trenutak) => {
  const company = ctx.company || {}
  return podnozje([
    company.CompanyNameShort || company.CompanyName || '',
    ctx.naslov || 'Otvorene stavke',
    dodatak,
    'stanje na ' + datum(ctx.doDatuma || new Date()),
    'ispis ' + trenutak
  ].filter(x => x).join(' · '))
}

const napomena = (text) => ({text: text, fontSize: 6, color: '#666666', margin: [0, 6, 0, 0]})

const NAPOMENA_STAVKE = 'Saldo = otvoreni računi − nepovezane uplate. Nepovezana uplata umanjuje saldo, ali nije ' +
  'povezana ni s jednim računom, pa račun s popisa može biti već plaćen. „Dana" su dani od dospijeća do datuma stanja.'

/**
 * Par.Otvoreno - otvorene stavke jednog partnera.
 * @param {Object} ctx  company, naslov, partner {ime, oib}, valuta, doDatuma, fromAccount,
 *                      toAccount, naDuguje, redci (sa saldom), sazetak, filtar
 */
export const buildOpenItemsPdf = (ctx) => {
  const cols = koloneOtvorenih()
  const valuta = ctx.valuta || 'EUR'
  const partner = ctx.partner || {}
  const redci = ctx.redci || []
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  redci.forEach(r => body.push(redOtvorene(cols, r, ctx.naDuguje)))
  const saldo = redci.length ? redci[redci.length - 1].saldo : 0
  body.push(redZbrojaOtvorenih(cols, 'UKUPNO ' + valuta + (ctx.filtar ? '  ·  prikaz: ' + ctx.filtar : ''), saldo, ctx.naDuguje, 8))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Otvorene stavke',
        (partner.ime || '') + (partner.oib ? '   OIB: ' + partner.oib : ''),
        uvjetiOtvorenih(ctx, valuta),
        ['Datum ispisa: ' + trenutak, 'Stavaka: ' + redci.length]
      ),
      sazetakOtvorenih(ctx.sazetak || {}, valuta, ctx.naDuguje),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice([1, body.length - 1])},
      napomena(NAPOMENA_STAVKE)
    ],
    footer: podnozjeOtvorenih(ctx, partner.ime || '', trenutak)
  }
}

export const printOpenItemsPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildOpenItemsPdf(ctx)).open()
}

const koloneOtvorenoPartneri = () => rasporedi([
  {key: 'partnerName', head: 'Partner', w: 150, align: 'left', flex: true},
  {key: 'partnerVATID', head: 'OIB', w: 56, align: 'left'},
  {key: 'racuna', head: 'Rač.', w: 24, align: 'right'},
  {key: 'iznosRacuna', head: 'Otvoreni računi', w: 70, align: 'right'},
  {key: 'uplata', head: 'Upl.', w: 24, align: 'right'},
  {key: 'iznosUplata', head: 'Nepovezane uplate', w: 70, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 80, align: 'right'},
  {key: 'nedospjelo', head: 'Nedospjelo', w: 62, align: 'right'},
  {key: 'd1_30', head: '1–30', w: 58, align: 'right'},
  {key: 'd31_60', head: '31–60', w: 58, align: 'right'},
  {key: 'd61_90', head: '61–90', w: 58, align: 'right'},
  {key: 'preko90', head: '>90 dana', w: 62, align: 'right'}
])

const IZNOSNE_PARTNERA = ['iznosRacuna', 'iznosUplata', 'nedospjelo', 'd1_30', 'd31_60', 'd61_90', 'preko90']

const redOtvorenoPartner = (cols, p, naDuguje, zbroj) => cols.map(c => {
  const o = {fontSize: zbroj ? 7.5 : 7, bold: !!zbroj, alignment: c.align, margin: zbroj ? [2, 3, 2, 3] : [2, 1.5, 2, 1.5]}
  if (c.key === 'saldo') { return Object.assign(o, {text: saldoTekst(p.saldo, naDuguje)}) }
  if (IZNOSNE_PARTNERA.indexOf(c.key) !== -1) {
    return Object.assign(o, {text: iznos(p[c.key]), color: !zbroj && c.key === 'preko90' && num(p[c.key]) > 0.005 ? CRVENA : undefined})
  }
  if (c.key === 'racuna' || c.key === 'uplata') { return Object.assign(o, {text: p[c.key] ? String(p[c.key]) : '–'}) }
  return Object.assign(o, {text: p[c.key] ? String(p[c.key]) : ''})
})

/**
 * Svi part.otv. - jedan redak po partneru, s razredima dospijeca.
 * @param {Object} ctx  company, naslov, valuta, doDatuma, fromAccount, toAccount,
 *                      naDuguje, partneri, ukupno, trazi
 */
export const buildOpenPartnersPdf = (ctx) => {
  const cols = koloneOtvorenoPartneri()
  const valuta = ctx.valuta || 'EUR'
  const partneri = ctx.partneri || []
  const ukupno = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  partneri.forEach(p => body.push(redOtvorenoPartner(cols, p, ctx.naDuguje, false)))
  body.push(redOtvorenoPartner(cols, Object.assign({}, ukupno, {partnerName: 'UKUPNO ' + valuta, partnerVATID: ''}), ctx.naDuguje, true))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Otvorene stavke',
        'Svi partneri' + (ctx.trazi ? '  ·  traži: „' + ctx.trazi + '"' : ''),
        uvjetiOtvorenih(ctx, valuta),
        ['Datum ispisa: ' + trenutak, 'Partnera: ' + partneri.length]
      ),
      sazetakOtvorenih(ukupno, valuta, ctx.naDuguje),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice([1, body.length - 1])},
      napomena('Saldo = otvoreni računi − nepovezane uplate. Razredi dospijeća odnose se na otvorene račune ' +
        '(dani od dospijeća do datuma stanja); dospjelo znači nepovezano, ne nužno neplaćeno.')
    ],
    footer: podnozjeOtvorenih(ctx, 'svi partneri', trenutak)
  }
}

export const printOpenPartnersPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildOpenPartnersPdf(ctx)).open()
}

/**
 * SVI DET. - otvoreni dokumenti svih partnera, partner po partner.
 * @param {Object} ctx  kao za listu partnera, plus grupe [{ime, oib, redci, sazetak}]
 *                      i ukupno (sazetak svih redaka)
 */
export const buildOpenPartnersDetailPdf = (ctx) => {
  const cols = koloneOtvorenih()
  const valuta = ctx.valuta || 'EUR'
  const grupe = ctx.grupe || []
  const ukupno = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  const jake = [1]
  grupe.forEach(g => {
    jake.push(body.length)
    body.push(redNaslovaGrupe(cols, g))
    g.redci.forEach(r => body.push(redOtvorene(cols, r, ctx.naDuguje)))
    body.push(redZbrojaOtvorenih(cols, 'Ukupno ' + (g.ime || ''), (g.sazetak || {}).saldo, ctx.naDuguje, 7.5))
  })
  jake.push(body.length)
  body.push(redZbrojaOtvorenih(cols, 'UKUPNO ' + valuta + '  ·  partnera ' + grupe.length, ukupno.saldo, ctx.naDuguje, 8))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Otvorene stavke',
        'Otvoreni dokumenti po partnerima' + (ctx.trazi ? '  ·  traži: „' + ctx.trazi + '"' : ''),
        uvjetiOtvorenih(ctx, valuta),
        ['Datum ispisa: ' + trenutak, 'Partnera: ' + grupe.length]
      ),
      sazetakOtvorenih(ukupno, valuta, ctx.naDuguje),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice(jake)},
      napomena(NAPOMENA_STAVKE)
    ],
    footer: podnozjeOtvorenih(ctx, 'po dokumentima', trenutak)
  }
}

export const printOpenPartnersDetailPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildOpenPartnersDetailPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Svi par. kartice - kartice svih partnera
// ---------------------------------------------------------------------------
//
// Razlika prema "Svi part.otvoreno": kartica pokazuje SVA knjizenja u razdoblju,
// pa su stupci promet (duguje, potrazuje) i saldo, bez razreda dospijeca.

const koloneKarticePartnera = () => rasporedi([
  {key: 'partnerName', head: 'Partner', w: 220, align: 'left', flex: true},
  {key: 'partnerVATID', head: 'OIB', w: 80, align: 'left'},
  {key: 'account4', head: 'Konto', w: 60, align: 'left'},
  {key: 'tDebit', head: 'Duguje', w: 100, align: 'right'},
  {key: 'tCredit', head: 'Potražuje', w: 100, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 110, align: 'right'}
])

const redKarticePartnera = (cols, p, naDuguje, zbroj) => cols.map(c => {
  const o = {fontSize: zbroj ? 8 : 7.5, bold: !!zbroj, alignment: c.align,
    margin: zbroj ? [2, 3, 2, 3] : [2, 1.5, 2, 1.5]}
  if (c.key === 'saldo') { return Object.assign(o, {text: saldoTekst(p.CreditDebit, naDuguje)}) }
  if (c.key === 'tDebit' || c.key === 'tCredit') { return Object.assign(o, {text: iznos(p[c.key])}) }
  return Object.assign(o, {text: p[c.key] ? String(p[c.key]) : ''})
})

const uvjetiKartica = (ctx, valuta) => valuta + ' · ' + datum(ctx.odDatuma) + ' – ' + datum(ctx.doDatuma) +
  ' · konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '')

const podnozjeKartica = (ctx, dodatak, trenutak) => {
  const company = ctx.company || {}
  return podnozje([
    company.CompanyNameShort || company.CompanyName || '',
    ctx.naslov || 'Kartice partnera',
    dodatak,
    datum(ctx.odDatuma) + ' – ' + datum(ctx.doDatuma),
    'ispis ' + trenutak
  ].filter(x => x).join(' · '))
}

const sazetakKartica = (z, valuta, naDuguje) => ({
  text: [
    {text: 'Duguje  ', fontSize: 7, color: '#666666'},
    {text: novac(z.duguje) + '     ', fontSize: 8, bold: true},
    {text: 'Potražuje  ', fontSize: 7, color: '#666666'},
    {text: novac(z.potrazuje) + '     ', fontSize: 8, bold: true},
    {text: 'Saldo  ', fontSize: 7, color: '#666666'},
    {text: saldoTekst(z.saldo, naDuguje) + ' ' + valuta, fontSize: 9, bold: true}
  ],
  margin: [0, 0, 0, 6]
})

/**
 * Svi par. kartice - jedan redak po partneru.
 * @param {Object} ctx company, naslov, valuta, odDatuma, doDatuma, fromAccount,
 *                     toAccount, naDuguje, partneri, ukupno {duguje,potrazuje,saldo}, trazi
 */
export const buildPartnerCardsPdf = (ctx) => {
  const cols = koloneKarticePartnera()
  const valuta = ctx.valuta || 'EUR'
  const partneri = ctx.partneri || []
  const ukupno = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  partneri.forEach(p => body.push(redKarticePartnera(cols, p, ctx.naDuguje, false)))
  body.push(redKarticePartnera(cols, {
    partnerName: 'UKUPNO ' + valuta, partnerVATID: '', account4: '',
    tDebit: ukupno.duguje, tCredit: ukupno.potrazuje, CreditDebit: ukupno.saldo
  }, ctx.naDuguje, true))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Kartice partnera',
        'Svi partneri' + (ctx.trazi ? '  ·  traži: „' + ctx.trazi + '"' : ''),
        uvjetiKartica(ctx, valuta),
        ['Datum ispisa: ' + trenutak, 'Partnera: ' + partneri.length]
      ),
      sazetakKartica(ukupno, valuta, ctx.naDuguje),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice([1, body.length - 1])},
      napomena('Saldo je razlika prometa duguje i potražuje u prikazanom razdoblju.')
    ],
    footer: podnozjeKartica(ctx, 'svi partneri', trenutak)
  }
}

export const printPartnerCardsPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildPartnerCardsPdf(ctx)).open()
}

const koloneKarticeStavke = () => rasporedi([
  {key: 'datum', head: 'Datum', w: 56, align: 'left'},
  {key: 'dokument', head: 'Dokument', w: 90, align: 'left'},
  {key: 'konto', head: 'Konto', w: 50, align: 'left'},
  {key: 'opis', head: 'Opis', w: 200, align: 'left', flex: true},
  {key: 'duguje', head: 'Duguje', w: 90, align: 'right'},
  {key: 'potrazuje', head: 'Potražuje', w: 90, align: 'right'},
  {key: 'saldo', head: 'Saldo', w: 100, align: 'right'}
])

const redKarticeStavke = (cols, r, naDuguje) => cols.map(c => {
  const o = {fontSize: 7, alignment: c.align, margin: [2, 1.5, 2, 1.5]}
  if (c.key === 'saldo') { return Object.assign(o, {text: saldoTekst(r.saldo, naDuguje)}) }
  if (c.key === 'duguje' || c.key === 'potrazuje') { return Object.assign(o, {text: iznos(r[c.key])}) }
  return Object.assign(o, {text: r[c.key] ? String(r[c.key]) : ''})
})

/**
 * Print SVI DET. za kartice - stavke svakog partnera, partner po partner.
 * @param {Object} ctx kao za listu, plus grupe [{ime, oib, redci, sazetak}]
 */
export const buildPartnerCardsDetailPdf = (ctx) => {
  const cols = koloneKarticeStavke()
  const valuta = ctx.valuta || 'EUR'
  const grupe = ctx.grupe || []
  const ukupno = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  const jake = [1]
  grupe.forEach(g => {
    jake.push(body.length)
    body.push(redNaslovaGrupe(cols, g))
    g.redci.forEach(r => body.push(redKarticeStavke(cols, r, ctx.naDuguje)))
    body.push(redZbrojaOtvorenih(cols, 'Ukupno ' + (g.ime || ''), (g.sazetak || {}).saldo, ctx.naDuguje, 7.5))
  })
  jake.push(body.length)
  body.push(redZbrojaOtvorenih(cols, 'UKUPNO ' + valuta + '  ·  partnera ' + grupe.length, ukupno.saldo, ctx.naDuguje, 8))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Kartice partnera',
        'Stavke po partnerima' + (ctx.trazi ? '  ·  traži: „' + ctx.trazi + '"' : ''),
        uvjetiKartica(ctx, valuta),
        ['Datum ispisa: ' + trenutak, 'Partnera: ' + grupe.length]
      ),
      sazetakKartica(ukupno, valuta, ctx.naDuguje),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice(jake)},
      napomena('Saldo u retku je tekuci saldo kartice partnera.')
    ],
    footer: podnozjeKartica(ctx, 'po stavkama', trenutak)
  }
}

export const printPartnerCardsDetailPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildPartnerCardsDetailPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Izvod otvorenih stavaka i opomena - dopis partneru
// ---------------------------------------------------------------------------
//
// Tekst je prenesen iz MainLedgerPrintCard3 (s ispravljenim tipfelerima), mijenja se raspored.
// Ista su i pravila: racun kojem rok placanja jos nije dosao ne ulazi, a datum
// u uvodnoj recenici je datum ispisa.

const DOPIS_MARGINE = [40, 36, 40, 44]
const DOPIS_WIDTH = 595.28 - DOPIS_MARGINE[0] - DOPIS_MARGINE[2]

const koloneDopisa = (valuta) => rasporedi([
  {key: 'rb', head: 'Rb.', w: 20, align: 'right'},
  {key: 'dokument', head: 'Broj dok.', w: 62, align: 'left'},
  {key: 'veza', head: 'Veza', w: 54, align: 'left'},
  {key: 'datumDok', head: 'Datum dokumenta', w: 50, align: 'left'},
  {key: 'dospijeceTxt', head: 'Datum dospijeća', w: 50, align: 'left'},
  {key: 'opis', head: 'Opis', w: 80, align: 'left', flex: true},
  {key: 'duguje', head: 'Duguje', w: 62, align: 'right'},
  {key: 'potrazuje', head: 'Potražuje', w: 62, align: 'right'},
  {key: 'saldo', head: 'Saldo ' + valuta, w: 66, align: 'right'}
], DOPIS_WIDTH)

// Potpis: naslov iznad crte, po zelji kratka napomena ispod.
const potpisDopisa = (naslov, ispod) => ({
  stack: [
    {text: naslov, fontSize: 9, alignment: 'center'},
    {canvas: [{type: 'line', x1: 12, y1: 36, x2: 225, y2: 36, lineWidth: 0.6, lineColor: '#444444'}]}
  ].concat(ispod ? [{text: ispod, fontSize: 7.5, color: '#444444', margin: [12, 6, 0, 0]}] : [])
})

// Tekst i crta za upis do kraja retka.
const crtaZaUpis = (tekst, margin) => ({
  table: {
    widths: ['auto', '*'],
    body: [[
      {text: tekst, fontSize: 9, border: [false, false, false, false]},
      {text: ' ', fontSize: 9, border: [false, false, false, true]}
    ]]
  },
  layout: {
    hLineWidth: () => 0.6,
    vLineWidth: () => 0,
    hLineColor: () => '#444444',
    paddingLeft: () => 0,
    paddingRight: () => 4,
    paddingTop: () => 0,
    paddingBottom: () => 1
  },
  margin: margin || [0, 0, 0, 0]
})

/**
 * @param {Object} ctx  vrsta ('izvod' | 'opomena'), company, partner (zapis iz sifrarnika),
 *                      valuta, naDuguje, fromAccount, toAccount, docFromDate, docToDate,
 *                      redci (iz otvoreneStavke.gradiRedke, po datumu)
 */
export const buildPartnerLetterPdf = (ctx) => {
  const opomena = ctx.vrsta === 'opomena'
  const company = ctx.company || {}
  const p = ctx.partner || {}
  const valuta = ctx.valuta || 'EUR'
  const naDuguje = ctx.naDuguje !== false
  const danas = dayjs()
  const trenutak = danas.format('DD.MM.YYYY HH:mm')
  const naslov = opomena ? 'O P O M E N A' : 'Izvod otvorenih stavaka ' + valuta
  const cols = koloneDopisa(valuta)

  // Kao i dosad: bez racuna kojima rok placanja jos nije dosao.
  const redci = (ctx.redci || []).filter(r => !(r.jeRacun && r.dospijece && dayjs(r.dospijece).isAfter(danas)))

  const body = [redNaslova(cols, 7)]
  let ukD = 0
  let ukP = 0
  let saldo = 0
  redci.forEach((r, i) => {
    const naD = r.jeRacun === naDuguje
    const d = naD ? num(r.otvoreno) : 0
    const pp = naD ? 0 : num(r.otvoreno)
    ukD += d
    ukP += pp
    saldo = Math.round((saldo + d - pp) * 100) / 100
    const s = saldo
    body.push(cols.map(c => {
      const o = {fontSize: 7.5, alignment: c.align, margin: [2, 2, 2, 2]}
      if (c.key === 'rb') { return Object.assign(o, {text: String(i + 1)}) }
      if (c.key === 'duguje') { return Object.assign(o, {text: iznos(d)}) }
      if (c.key === 'potrazuje') { return Object.assign(o, {text: iznos(pp)}) }
      if (c.key === 'saldo') { return Object.assign(o, {text: novac(s)}) }
      if (c.key === 'datumDok') { return Object.assign(o, {text: r.datumDokTxt || r.datumTxt || ''}) }
      return Object.assign(o, {text: r[c.key] ? String(r[c.key]) : ''})
    }))
  })
  const spanDo = cols.length - 3
  const zbroj = (text, align) => ({text: text, fontSize: 8, bold: true, alignment: align, margin: [2, 3, 2, 3]})
  const ukupno = [Object.assign(zbroj('UKUPNO:', 'left'), {colSpan: spanDo})]
  for (let i = 1; i < spanDo; i += 1) { ukupno.push({text: ''}) }
  ukupno.push(zbroj(novac(ukD), 'right'))
  ukupno.push(zbroj(novac(ukP), 'right'))
  ukupno.push(zbroj(novac(saldo), 'right'))
  body.push(ukupno)

  const zaglavljeDopisa = {
    columns: [
      {
        width: '*',
        text: [
          {text: (company.CompanyName || company.CompanyNameShort || '') + '\n', fontSize: 12, bold: true},
          {text: [company.CompanyStreet, company.CompanyBuildingNameNumber].filter(x => x).join(' ') + '\n', fontSize: 8},
          {text: [company.CompanyPostcode, company.CompanyCity].filter(x => x).join(' ') + '\n', fontSize: 8},
          {text: [company.CompanyPhones, company.Companyemail].filter(x => x).join('   ') + '\n', fontSize: 8},
          {text: 'OIB: ' + (company.CompanyVATID || ''), fontSize: 8}
        ]
      },
      {
        width: 150,
        alignment: 'right',
        fontSize: 7.5,
        color: '#444444',
        text: [
          'Od konta : ' + (ctx.fromAccount || '') + '\n',
          'Do konta : ' + (ctx.toAccount || '') + '\n',
          'Od datuma: ' + datum(ctx.docFromDate) + '\n',
          'Do datuma: ' + datum(ctx.docToDate) + '\n',
          'Datum ispisa: ' + trenutak
        ]
      }
    ],
    columnGap: 12
  }

  // Adresa partnera stoji gdje je i prije bila - ispod zaglavlja, s lijeve strane.
  const adresa = {
    margin: [14, 30, 0, 30],
    stack: [
      {text: p.partnerName || '', fontSize: 11, bold: true},
      {text: [p.partnerStreet, p.partnerBuildingNameNumber].filter(x => x).join(' '), fontSize: 9},
      {text: [p.partnerPostcode, p.partnerCity].filter(x => x).join(' '), fontSize: 9},
      {text: p.partnerVATID ? 'OIB ' + p.partnerVATID : '', fontSize: 9}
    ]
  }

  const zavrsetak = opomena
    ? [
      {text: 'Valuta: ' + valuta, fontSize: 10, margin: [0, 12, 0, 0]},
      {text: 'Navedeni dug potrebno je uplatiti u roku od 3 radna dana, a potvrdu o plaćanju molim dostavite na email. Hvala.', fontSize: 10, margin: [0, 8, 0, 0]},
      {text: 'Ukoliko je navedeni dug u međuvremenu podmiren, primite naše isprike i zanemarite ovu obavijest.', fontSize: 10, margin: [0, 2, 0, 0]},
      {text: 'S poštovanjem,', fontSize: 10, margin: [0, 14, 0, 0]},
      {columns: [{width: '*', text: ''}, {width: 240, stack: [potpisDopisa('')]}], margin: [0, 4, 0, 0]}
    ]
    : [
      {
        columns: [potpisDopisa('Pošiljatelj izvoda'), potpisDopisa('Potvrđujemo usklađenost otvorenih stavaka')],
        columnGap: 24,
        margin: [0, 22, 0, 0]
      },
      crtaZaUpis('Napomena dužnika: Osporavamo iskazano za iznos', [0, 30, 0, 0]),
      crtaZaUpis('iz sljedećih razloga', [0, 16, 0, 0]),
      crtaZaUpis(' ', [0, 16, 0, 0]),
      {
        columns: [
          potpisDopisa('Mjesto i datum', 'Molimo da ovjereni primjerak ovog izvoda vratite vjerovniku'),
          {text: ''}
        ],
        columnGap: 24,
        margin: [0, 22, 0, 0]
      }
    ]

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: DOPIS_MARGINE,
    content: [
      zaglavljeDopisa,
      {canvas: [{type: 'line', x1: 0, y1: 6, x2: DOPIS_WIDTH, y2: 6, lineWidth: 0.8, lineColor: LINE_JAKA}]},
      adresa,
      {text: naslov, fontSize: opomena ? 20 : 16, bold: true, margin: [0, 0, 0, 12]},
      {text: 'Poštovani,', fontSize: 10},
      {
        text: 'Uvidom u evidenciju dugovanja na dan ' + danas.format('DD.MM.YYYY') + ' utvrdili smo sljedeće otvorene stavke',
        fontSize: 10,
        margin: [0, 8, 0, 10]
      },
      // Dopis ide partneru - redak se ne prelama preko dvije stranice.
      {table: {headerRows: 1, dontBreakRows: true, widths: cols.map(c => c.w), body: body}, layout: layoutTablice([1, body.length - 1])},
      {stack: zavrsetak, unbreakable: true}
    ],
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          width: '*',
          text: [company.CompanyNameShort || company.CompanyName || '', opomena ? 'Opomena' : 'Izvod otvorenih stavaka',
            p.partnerName || '', 'ispis ' + trenutak].filter(x => x).join(' · '),
          fontSize: 6,
          color: '#666666'
        },
        {width: 'auto', text: 'Stranica ' + currentPage + ' od ' + pageCount, fontSize: 6, alignment: 'right'}
      ],
      columnGap: 12,
      margin: [DOPIS_MARGINE[0], 14, DOPIS_MARGINE[2], 0]
    })
  }
}

export const printPartnerLetterPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildPartnerLetterPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Dnevnik knjizenja
// ---------------------------------------------------------------------------
//
// Jedan redak po dokumentu, kronoloski. U detaljnom ispisu ispod dokumenta idu
// njegove stavke u istoj mrezi kolona: konto pod datumom, naziv konta preko
// vrste i dokumenta, opis stavke pod partnerom, iznosi pod iznosima.

const koloneDnevnika = () => rasporedi([
  {key: 'rb', head: 'Rb.', w: 30, align: 'right'},
  {key: 'datumTxt', head: 'Datum knjiž.', w: 50, align: 'left'},
  {key: 'vrsta', head: 'Vrsta', w: 54, align: 'left'},
  {key: 'oznaka', head: 'Dokument', w: 74, align: 'left'},
  {key: 'opis', head: 'Partner / opis', w: 150, align: 'left', flex: true},
  {key: 'stavaka', head: 'Stavaka', w: 34, align: 'right'},
  {key: 'duguje', head: 'Duguje', w: 76, align: 'right'},
  {key: 'potrazuje', head: 'Potražuje', w: 76, align: 'right'},
  {key: 'knjizio', head: 'Knjižio', w: 90, align: 'left'},
  {key: 'unesenoTxt', head: 'Uneseno', w: 62, align: 'left'}
])

const redDokumentaDnevnika = (cols, d, detaljno) => cols.map(c => {
  const o = {fontSize: 7, bold: !!detaljno, alignment: c.align, margin: detaljno ? [2, 3, 2, 2] : [2, 1.5, 2, 1.5]}
  if (c.key === 'duguje' || c.key === 'potrazuje') { return Object.assign(o, {text: novac(d[c.key])}) }
  if (c.key === 'vrsta') { return Object.assign(o, {text: d.vrsta ? d.vrsta.naziv : ''}) }
  if (c.key === 'opis') { return Object.assign(o, {text: [d.partnerName, d.opis].filter(x => x).join(' · ')}) }
  if (c.key === 'oznaka' && d.neuravnotezen) {
    return Object.assign(o, {text: [
      {text: d.oznaka || ''},
      {text: '\nrazlika ' + novac(Math.abs(num(d.razlika))), color: CRVENA, bold: true, fontSize: 6.5}
    ]})
  }
  return Object.assign(o, {text: d[c.key] === null || d[c.key] === undefined ? '' : String(d[c.key])})
})

// Redoslijed celija prati koloneDnevnika (10 kolona).
const redStavkeDnevnika = (s) => {
  const o = (text, align, extra) => Object.assign({text: text, fontSize: 6.5, color: '#333333', alignment: align, margin: [2, 1, 2, 1]}, extra || {})
  const opis = [s.Description, s.partnerName, s.veza].map(x => String(x || '').trim()).filter(x => x).join(' · ')
  return [
    o('', 'left'),
    o(s.account4 || '', 'left'),
    o(s.naziv || '', 'left', {colSpan: 2}), {text: ''},
    o(opis, 'left'),
    o('', 'right'),
    o(iznos(s.duguje), 'right'),
    o(iznos(s.potrazuje), 'right'),
    o('', 'left', {colSpan: 2}), {text: ''}
  ]
}

/**
 * @param {Object} ctx  company, valuta, razdoblje, godina, redoslijed, filtar,
 *                      dokumenti (s ekrana), zbroj, detaljno, stavke {idDokumenta: [stavke]}
 */
export const buildJournalPdf = (ctx) => {
  const cols = koloneDnevnika()
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const dokumenti = ctx.dokumenti || []
  const z = ctx.zbroj || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')
  const naslov = ctx.detaljno ? 'Dnevnik knjiženja — detaljno' : 'Dnevnik knjiženja'

  const body = [redNaslova(cols, 7)]
  const jake = [1]
  dokumenti.forEach(d => {
    if (ctx.detaljno && body.length > 1) { jake.push(body.length) }
    body.push(redDokumentaDnevnika(cols, d, ctx.detaljno))
    if (ctx.detaljno) {
      ((ctx.stavke || {})[d.id] || []).forEach(s => body.push(redStavkeDnevnika(s)))
    }
  })
  jake.push(body.length)
  const zbrojCelija = (text, align, extra) => Object.assign({text: text, fontSize: 8, bold: true, alignment: align, margin: [2, 3, 2, 3]}, extra || {})
  body.push([
    zbrojCelija('UKUPNO ' + valuta + '  ·  ' + dokumenti.length + ' dokumenata', 'left', {colSpan: 5}),
    {text: ''}, {text: ''}, {text: ''}, {text: ''},
    zbrojCelija(String(z.stavaka || 0), 'right'),
    zbrojCelija(novac(z.duguje), 'right'),
    zbrojCelija(novac(z.potrazuje), 'right'),
    zbrojCelija('', 'left', {colSpan: 2}), {text: ''}
  ])

  const sive = (t) => ({text: t, fontSize: 7, color: '#666666'})
  const jako = (t) => ({text: t, fontSize: 8, bold: true})
  const razlika = Math.abs(num(z.razlika)) < 0.005 ? 'u ravnoteži' : novac(Math.abs(num(z.razlika)))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        company,
        naslov,
        (ctx.razdoblje || '') + '   ·   poslovna godina ' + (ctx.godina || ''),
        valuta + ' · ' + (ctx.redoslijed || '') + (ctx.filtar ? ' · ' + ctx.filtar : ''),
        ['Datum ispisa: ' + trenutak, 'Dokumenata: ' + dokumenti.length]
      ),
      {
        text: [
          sive('Dokumenata  '), jako(dokumenti.length + '     '),
          sive('Stavaka  '), jako((z.stavaka || 0) + '     '),
          sive('Duguje  '), jako(novac(z.duguje) + '     '),
          sive('Potražuje  '), jako(novac(z.potrazuje) + '     '),
          sive('Razlika  '), jako(razlika + '     '),
          sive('Neuravnoteženih  '), jako(String(z.neuravnotezenih || 0))
        ],
        margin: [0, 0, 0, 6]
      },
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice(jake)}
    ],
    footer: podnozje([
      company.CompanyNameShort || company.CompanyName || '',
      naslov,
      ctx.razdoblje || '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }
}

export const printJournalPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildJournalPdf(ctx)).open()
}

// ---------------------------------------------------------------------------
// Svi par.ot.banka - otvoreno po razredima dospijeca
// ---------------------------------------------------------------------------
//
// Lista za banku: jedan redak po partneru, a iznosi su razvrstani po tome
// koliko je dugo stavka dospjela. Server vec vraca gotove razrede (Total30 ...
// TotalOver), ovdje se samo ispisuju i zbrajaju.

const RAZREDI_BANKA_PDF = [
  {key: 'Total30', head: 'Do 30 d'},
  {key: 'Total60', head: '30–60 d'},
  {key: 'Total90', head: '60–90 d'},
  {key: 'Total120', head: '90–120 d'},
  {key: 'TotalOver', head: 'Preko 120 d'}
]

const koloneBanka = () => rasporedi([
  {key: 'partnerName', head: 'Partner', w: 220, align: 'left', flex: true},
  {key: 'partnerVATID', head: 'OIB', w: 80, align: 'left'},
  {key: 'account4', head: 'Konto', w: 55, align: 'left'}
].concat(RAZREDI_BANKA_PDF.map(r => ({key: r.key, head: r.head, w: 78, align: 'right'})))
  .concat([{key: 'sve', head: 'Ukupno', w: 90, align: 'right'}]))

const redBanka = (cols, p, zbroj) => cols.map(c => {
  const o = {fontSize: zbroj ? 8 : 7.5, bold: !!zbroj, alignment: c.align,
    margin: zbroj ? [2, 3, 2, 3] : [2, 1.5, 2, 1.5]}
  if (c.key === 'sve') { return Object.assign(o, {text: novac(p.sve)}) }
  if (c.key === 'TotalOver') {
    // Najstarije dospjelo je ono zbog cega se lista i gleda, pa je crveno.
    return Object.assign(o, {text: iznos(p[c.key]),
      color: !zbroj && num(p[c.key]) > 0.005 ? CRVENA : undefined})
  }
  if (RAZREDI_BANKA_PDF.some(r => r.key === c.key)) { return Object.assign(o, {text: iznos(p[c.key])}) }
  return Object.assign(o, {text: p[c.key] ? String(p[c.key]) : ''})
})

const sazetakBanka = (z, valuta) => ({
  text: RAZREDI_BANKA_PDF.map(r => [
    {text: r.head + '  ', fontSize: 7, color: '#666666'},
    {text: novac(z[r.key]) + '     ', fontSize: 8, bold: true}
  ]).reduce((a, b) => a.concat(b), []).concat([
    {text: 'Ukupno otvoreno  ', fontSize: 7, color: '#666666'},
    {text: novac(z.sve) + ' ' + valuta, fontSize: 9, bold: true}
  ]),
  margin: [0, 0, 0, 6]
})

/**
 * Svi par.ot.banka - jedan redak po partneru, iznosi po razredima dospijeca.
 * @param {Object} ctx company, naslov, valuta, doDatuma, fromAccount, toAccount,
 *                     BusinessYear, partneri, ukupno (razredi + sve), trazi
 */
export const buildBankPartnersPdf = (ctx) => {
  const cols = koloneBanka()
  const valuta = ctx.valuta || 'EUR'
  const partneri = ctx.partneri || []
  const ukupno = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  partneri.forEach(p => body.push(redBanka(cols, p, false)))
  body.push(redBanka(cols, Object.assign({}, ukupno, {partnerName: 'UKUPNO ' + valuta,
    partnerVATID: '', account4: ''}), true))

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      zaglavlje(
        ctx.company || {},
        ctx.naslov || 'Otvorene stavke po dospijeću',
        'Svi partneri' + (ctx.trazi ? '  ·  traži: „' + ctx.trazi + '"' : ''),
        valuta + ' · po dospijeću · konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '') +
          (ctx.BusinessYear ? ' · poslovna godina ' + ctx.BusinessYear : ''),
        ['Datum ispisa: ' + trenutak, 'Partnera: ' + partneri.length]
      ),
      sazetakBanka(ukupno, valuta),
      {table: {headerRows: 1, widths: cols.map(c => c.w), body: body}, layout: layoutTablice([1, body.length - 1])},
      napomena('Razredi pokazuju koliko je dugo stavka dospjela, računano od roka plaćanja. ' +
        'Iznosi su otvoreni dio stavke; dospjelo znači nepovezano, ne nužno neplaćeno.')
    ],
    footer: podnozje([
      (ctx.company || {}).CompanyNameShort || (ctx.company || {}).CompanyName || '',
      ctx.naslov || 'Otvorene stavke po dospijeću',
      'po dospijeću',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }
}

export const printBankPartnersPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildBankPartnersPdf(ctx)).open()
}

export default {
  buildJournalPdf, printJournalPdf,
  buildPartnerLetterPdf, printPartnerLetterPdf,
  buildBalancePdf, printBalancePdf,
  buildAccountCardPdf, printAccountCardPdf,
  buildStatementPdf, printStatementPdf,
  buildPartnerCardPdf, printPartnerCardPdf,
  buildOpenItemsPdf, printOpenItemsPdf,
  buildOpenPartnersPdf, printOpenPartnersPdf,
  buildOpenPartnersDetailPdf, printOpenPartnersDetailPdf,
  buildBankPartnersPdf, printBankPartnersPdf
}
